import React, { useState, useContext } from "react";
import { Helmet } from 'react-helmet-async';
import { Typography, Box, Grid, TextField, Button, Paper, Divider, Link, Container } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import Footer from "../template/Footer";
import { AuthContext, FireactContext, SetPageTitle } from "../lib/core";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { firestoreInstance } = useContext(AuthContext);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    // basic client-side validation
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError("Please fill out all fields.");
      return;
    }

    if (!firestoreInstance) {
      setError("System error: database not available.");
      return;
    }

    try {
      setIsSubmitting(true);
      await addDoc(collection(firestoreInstance, "website_contact"), {
        name: form.name,
        email: form.email,
        message: form.message,
        createdAt: serverTimestamp(),
      });
      setSubmitted(true);
    } catch (err) {
      console.error("Contact form submit error:", err);
      setError(err?.message || "Failed to send message. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Helmet>
        <title>Contact — Living Waters</title>
        <meta name="description" content="Contact Living Waters — send feedback, questions, or support requests." />
        <meta property="og:title" content="Contact — Living Waters" />
      </Helmet>
      <Box sx={{ p: 4 }}>
      <Typography variant="h4" gutterBottom>Contact Us</Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        We'd love to hear from you! Fill out the form or reach us directly.
      </Typography>
      <Grid container spacing={4}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 2 }}>
            {submitted ? (
              <Typography color="primary" variant="h6">Thank you for your message!</Typography>
            ) : (
              <form onSubmit={handleSubmit}>
                <TextField
                  label="Name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  fullWidth
                  required
                  margin="normal"
                />
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  fullWidth
                  required
                  margin="normal"
                />
                <TextField
                  label="Message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  fullWidth
                  required
                  margin="normal"
                  multiline
                  minRows={4}
                />
                {error && <Typography color="error" sx={{ mt: 1 }}>{error}</Typography>}
                <Button type="submit" variant="contained" sx={{ mt: 2 }} fullWidth disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 2, bgcolor: 'grey.50' }}>
            <Typography variant="h6" gutterBottom>Contact Information</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <EmailIcon sx={{ mr: 1 }} color="secondary" />
              <Typography variant="body2">info@livingwaters.biz</Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Follow Us</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Link href="#" color="inherit"><FacebookIcon color="secondary" /></Link>
              <Link href="#" color="inherit"><TwitterIcon color="secondary" /></Link>
              <Link href="#" color="inherit"><LinkedInIcon color="secondary" /></Link>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      </Box>
      <Footer />
    </Container>
  );
};

export default ContactPage;